import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface GlobalDialogData {
  title: string
  content: string
  contentClass?: string
  isHtml?: boolean
  showConfirmButton?: boolean
  cancelButtonClass?: string
}

/** 全域對話框 Store，管理 API 錯誤訊息及確認對話框 */
export const useGlobalDialogStore = defineStore('global-dialog', () => {
  const _isDialogShow = ref(false)

  const _globalDialogData = ref<GlobalDialogData | null>(null)

  const _ifConfirm = ref<((value: boolean) => void) | null>(null)

  /** 當前對話框內容資料 */
  const globalDialogData = computed(() => {
    return _globalDialogData.value
  })

  /** 對話框是否顯示中 */
  const isDialogShow = computed(() => {
    return _isDialogShow.value
  })

  /**
   * 顯示確認對話框，回傳 Promise<boolean>，
   * 讓呼叫方可以 await 等待使用者決定（true: 確認, false: 取消/關閉）
   */
  const openConfirmDialog = (data: GlobalDialogData): Promise<boolean> => {
    return new Promise((ifConfirm) => {
      _globalDialogData.value = {
        ...data,
        showConfirmButton: true,
        cancelButtonClass:
          data.cancelButtonClass || 'btn btn-outline-secondary',
        contentClass: data.contentClass || 'w-100 d-flex justify-content-center'
      }
      _isDialogShow.value = true
      _ifConfirm.value = ifConfirm
    })
  }

  /** 關閉對話框並 resolve false */
  const closeGlobalDialog = () => {
    _ifConfirm.value?.(false)
    _globalDialogData.value = null
    _isDialogShow.value = false
    _ifConfirm.value = null
  }

  /** 使用者點擊確認，resolve true 並關閉對話框 */
  const confirmDialog = () => {
    // 先清空 _ifConfirm 再呼叫 closeGlobalDialog，避免 close 內部觸發第二次 false resolve
    const ifConfirm = _ifConfirm.value
    _ifConfirm.value = null
    closeGlobalDialog()
    ifConfirm?.(true)
  }

  /** 使用者點擊取消 */
  const cancelDialog = () => {
    closeGlobalDialog()
  }

  return {
    /** 當前對話框內容資料 */
    globalDialogData,
    /** 對話框是否顯示中 */
    isDialogShow,
    /** 顯示確認對話框，回傳 Promise<boolean> */
    openConfirmDialog,
    /** 關閉對話框並 resolve false */
    closeGlobalDialog,
    /** 使用者點擊確認，resolve true 並關閉對話框 */
    confirmDialog,
    /** 使用者點擊取消 */
    cancelDialog
  }
})
